import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { NumericInput } from '@interstate/components/NumericInput';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { useEffect, useRef, useState, type FC } from 'react';
import { INPUT_INVALID_ERROR_MESSAGE } from '@makemydeal/dr-dash-components';
import { parseDownPayment } from '../utils/downPaymentValidation';

export interface DownPaymentRowInputProps {
    downPayment: number;
    rowIndex: number;
    allDownPayments: number[];
    onDownPaymentChange: (index: number, newValue: number) => void;
    isLoading: boolean;
    /** Flag the input (matches the main grid) when Payment Service returned no rates for this row. */
    hasError?: boolean;
    /** Make the input read-only (e.g. at max scenarios, when a structure can't be chosen). */
    readOnly?: boolean;
    testId?: string;
}

export const DownPaymentRowInput: FC<DownPaymentRowInputProps> = ({
    downPayment,
    rowIndex,
    allDownPayments,
    onDownPaymentChange,
    isLoading,
    hasError = false,
    readOnly = false,
    testId
}) => {
    const [editValue, setEditValue] = useState(String(downPayment));
    const [resetKey, setResetKey] = useState(0);
    const isEditingRef = useRef(false);
    const isEscapingRef = useRef(false);
    // TextInputBase calls onChange on blur to commit the formatted value.
    // Set this flag before reverting so the spurious onChange doesn't overwrite the reverted editValue.
    const isRevertingRef = useRef(false);

    useEffect(() => {
        if (!isEditingRef.current) {
            setEditValue(String(downPayment));
        }
    }, [downPayment]);

    const commitDownPayment = () => {
        const enteredDownPayment = parseDownPayment(editValue);
        const isDuplicate =
            enteredDownPayment !== null &&
            enteredDownPayment !== downPayment &&
            allDownPayments.some((dp, i) => i !== rowIndex && dp === enteredDownPayment);

        if (enteredDownPayment !== null && !isDuplicate && enteredDownPayment !== downPayment) {
            onDownPaymentChange(rowIndex, enteredDownPayment);
        } else {
            if (editValue !== String(downPayment)) {
                isRevertingRef.current = true;
                setResetKey((k) => k + 1);
            }
            setEditValue(String(downPayment));
        }
        isEditingRef.current = false;
    };

    const handleChange = (e: InterstateOnChangeEvent<TextInputEventValue>) => {
        if (isRevertingRef.current) {
            isRevertingRef.current = false;
            return;
        }
        setEditValue(e.target.value);
    };

    const handleFocus = () => {
        isEditingRef.current = true;
        isRevertingRef.current = false;
    };

    const handleBlur = () => {
        if (isEscapingRef.current) {
            isEscapingRef.current = false;
            return;
        }
        commitDownPayment();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
            isEscapingRef.current = true;
            isEditingRef.current = false;
            if (editValue !== String(downPayment)) {
                isRevertingRef.current = true;
                setEditValue(String(downPayment));
                setResetKey((k) => k + 1);
            }
            (e.target as HTMLInputElement).blur();
        }
    };

    return (
        <NumericInput
            key={resetKey}
            size="small"
            displayLabel={false}
            label={`Down payment ${downPayment}`}
            name={`down-payment-input-${rowIndex}`}
            inputPrefix="$"
            autoInsertCommas
            allowDecimal={false}
            disabled={isLoading}
            readOnly={readOnly}
            hasError={hasError}
            errorMessage={INPUT_INVALID_ERROR_MESSAGE}
            value={editValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            data-testid={testId}
        />
    );
};
